/**
 * Content API Client
 */

import type {
  Post,
  PostListResponse,
  CreatePostRequest,
  UpdatePostRequest,
  AddMediaRequest,
  MediaItem,
  Annotation,
  CreateAnnotationRequest,
  UpdateAnnotationRequest,
  Reference,
  CreateReferenceRequest,
  UpdateReferenceRequest,
  UserProfile,
  ProfileCreateRequest,
  RevisionListItem,
  RevisionDetail,
  StoryListResponse,
  StoryLanguagesResponse,
  Collection,
  CreateCollectionRequest,
  UpdateCollectionRequest,
  PostLanguagesResponse,
  PostProductCollaborationStatuses,
} from '../types';
import { CONTENT_API, STORAGE_API, STORAGE_API_KEY } from '../config';
import { getAccessToken, refreshAccessToken } from '../auth';

const API_BASE_URL = CONTENT_API + '/api/v1';
const API_KEY = (import.meta.env.VITE_CONTENT_API_KEY || '').trim();

class ContentApiClient {
  private baseUrl: string;

  constructor(baseUrl: string = API_BASE_URL) {
    this.baseUrl = baseUrl;
  }

  private async request<T>(
    endpoint: string,
    options: RequestInit = {},
    _retry = false,
  ): Promise<T> {
    const url = `${this.baseUrl}${endpoint}`;
    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
      ...(options.headers as Record<string, string> | undefined),
    };
    const token = getAccessToken();
    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    }
    if (API_KEY) {
      headers['X-API-KEY'] = API_KEY;
    }
    const response = await fetch(url, {
      ...options,
      headers,
    });

    // 401 once → try a token refresh, then retry once
    if (response.status === 401 && !_retry && token) {
      const refreshed = await refreshAccessToken();
      if (refreshed) {
        return this.request<T>(endpoint, options, true);
      }
    }

    if (!response.ok) {
      const error = await response.text();
      throw new Error(`API Error: ${response.status} - ${error}`);
    }

    // Handle empty responses (204 No Content)
    const contentType = response.headers.get('content-type');
    if (response.status === 204 || !contentType?.includes('application/json')) {
      return {} as T;
    }

    return response.json();
  }

  // Posts
  async listPosts(params?: {
    status?: string;
    doc_type?: string;
    author_id?: string;
    content_type?: string;
    search?: string;
    limit?: number;
    offset?: number;
  }): Promise<PostListResponse> {
    const searchParams = new URLSearchParams();
    if (params) {
      Object.entries(params).forEach(([key, value]) => {
        if (value !== undefined) {
          searchParams.set(key, String(value));
        }
      });
    }
    const query = searchParams.toString();
    return this.request<PostListResponse>(`/posts/${query ? `?${query}` : ''}`);
  }

  async getPost(postId: number): Promise<Post> {
    return this.request<Post>(`/posts/${postId}`);
  }

  async createPost(data: CreatePostRequest): Promise<Post> {
    return this.request<Post>('/posts/', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async updatePost(postId: number, data: UpdatePostRequest): Promise<Post> {
    return this.request<Post>(`/posts/${postId}`, {
      method: 'PATCH',
      body: JSON.stringify(data),
    });
  }

  async deletePost(postId: number): Promise<void> {
    await this.request<{ success: boolean }>(`/posts/${postId}`, {
      method: 'DELETE',
    });
  }

  // Media
  async listMedia(postId: number): Promise<MediaItem[]> {
    return this.request<MediaItem[]>(`/posts/${postId}/media/`);
  }

  async addMedia(postId: number, data: AddMediaRequest): Promise<MediaItem> {
    return this.request<MediaItem>(`/posts/${postId}/media/`, {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async deleteMedia(postId: number, mediaId: number): Promise<void> {
    await this.request<{ success: boolean }>(
      `/posts/${postId}/media/${mediaId}`,
      {
        method: 'DELETE',
      }
    );
  }

  // Annotations
  async listAnnotations(postId: number): Promise<Annotation[]> {
    return this.request<Annotation[]>(`/posts/${postId}/annotations/`);
  }

  async createAnnotation(
    postId: number,
    data: CreateAnnotationRequest
  ): Promise<Annotation> {
    return this.request<Annotation>(`/posts/${postId}/annotations/`, {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async updateAnnotation(
    postId: number,
    annotationId: number,
    data: UpdateAnnotationRequest
  ): Promise<Annotation> {
    return this.request<Annotation>(
      `/posts/${postId}/annotations/${annotationId}`,
      {
        method: 'PATCH',
        body: JSON.stringify(data),
      }
    );
  }

  async deleteAnnotation(postId: number, annotationId: number): Promise<void> {
    await this.request<{ success: boolean }>(
      `/posts/${postId}/annotations/${annotationId}`,
      {
        method: 'DELETE',
      }
    );
  }

  // References
  async listReferences(postId: number): Promise<Reference[]> {
    return this.request<Reference[]>(`/posts/${postId}/references/`);
  }

  async createReference(
    postId: number,
    data: CreateReferenceRequest
  ): Promise<Reference> {
    return this.request<Reference>(`/posts/${postId}/references/`, {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async updateReference(
    postId: number,
    referenceId: number,
    data: UpdateReferenceRequest
  ): Promise<Reference> {
    return this.request<Reference>(
      `/posts/${postId}/references/${referenceId}`,
      {
        method: 'PATCH',
        body: JSON.stringify(data),
      }
    );
  }

  async deleteReference(postId: number, referenceId: number): Promise<void> {
    await this.request<{ success: boolean }>(
      `/posts/${postId}/references/${referenceId}`,
      {
        method: 'DELETE',
      }
    );
  }

  // Revisions
  async listRevisions(postId: number): Promise<RevisionListItem[]> {
    return this.request<RevisionListItem[]>(`/posts/${postId}/revisions/`);
  }

  async getLatestRevision(postId: number): Promise<RevisionDetail | null> {
    try {
      return await this.request<RevisionDetail>(`/posts/${postId}/revisions/latest`);
    } catch {
      return null;
    }
  }

  async getRevision(postId: number, revisionId: number): Promise<RevisionDetail> {
    return this.request<RevisionDetail>(`/posts/${postId}/revisions/${revisionId}`);
  }

  async getPostProductCollaborationStatuses(
    postId: number,
  ): Promise<PostProductCollaborationStatuses> {
    return this.request<PostProductCollaborationStatuses>(
      `/posts/${postId}/products/collaboration-status`,
    );
  }

  // Profiles
  async getProfile(authorId: string): Promise<UserProfile> {
    return this.request<UserProfile>(`/profiles/${encodeURIComponent(authorId)}`);
  }

  async upsertProfile(authorId: string, data: ProfileCreateRequest): Promise<UserProfile> {
    return this.request<UserProfile>(`/profiles/${encodeURIComponent(authorId)}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  }

  // Stories
  async listStories(language?: string): Promise<StoryListResponse> {
    const searchParams = new URLSearchParams();
    if (language) {
      searchParams.set('language', language);
    }
    const query = searchParams.toString();
    return this.request<StoryListResponse>(`/stories/${query ? `?${query}` : ''}`);
  }

  async getStoryLanguages(storyGroup: string): Promise<StoryLanguagesResponse> {
    return this.request<StoryLanguagesResponse>(
      `/stories/${encodeURIComponent(storyGroup)}/languages/`
    );
  }

  async generateStoryLanguages(storyGroup: string, languages: string[], forceRefresh = false): Promise<unknown> {
    return this.request<unknown>(
      `/stories/${encodeURIComponent(storyGroup)}/generate-languages/`,
      {
        method: 'POST',
        body: JSON.stringify({ languages, force_refresh: forceRefresh }),
      }
    );
  }

  // Collections
  async listCollections(): Promise<Collection[]> {
    return this.request<Collection[]>('/collections/');
  }

  async getCollection(slug: string): Promise<Collection & { posts: Post[] }> {
    return this.request<Collection & { posts: Post[] }>(`/collections/${slug}`);
  }

  async createCollection(data: CreateCollectionRequest): Promise<Collection> {
    return this.request<Collection>('/collections/', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async updateCollection(collectionId: number, data: UpdateCollectionRequest): Promise<Collection> {
    return this.request<Collection>(`/collections/${collectionId}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  }

  async deleteCollection(collectionId: number): Promise<void> {
    await this.request<{ success: boolean }>(`/collections/${collectionId}`, {
      method: 'DELETE',
    });
  }

  async addPostToCollection(collectionId: number, postId: number): Promise<void> {
    await this.request<unknown>(`/collections/${collectionId}/posts/${postId}`, {
      method: 'POST',
    });
  }

  async removePostFromCollection(collectionId: number, postId: number): Promise<void> {
    await this.request<unknown>(`/collections/${collectionId}/posts/${postId}`, {
      method: 'DELETE',
    });
  }

  async getPostCollections(postId: number): Promise<Collection[]> {
    return this.request<Collection[]>(`/posts/${postId}/collections/`);
  }

  // Health
  async health(): Promise<{ status: string }> {
    return this.request<{ status: string }>('/health');
  }

  async getPostLanguages(postId: number): Promise<PostLanguagesResponse | null> {
    try {
      return await this.request<PostLanguagesResponse>(`/posts/${postId}/languages`);
    } catch {
      return null;
    }
  }

  async translatePost(
    postId: number,
    targetLanguages: string[],
    forceRefresh: boolean = false
  ): Promise<{
    language_group: string;
    source_language: string;
    results: Array<{ language: string; post_id?: number; status: string; error?: string }>;
  }> {
    return this.request(`/posts/${postId}/translate`, {
      method: 'POST',
      body: JSON.stringify({
        target_languages: targetLanguages,
        force_refresh: forceRefresh,
      }),
    });
  }

  /**
   * Fetch a post as PDF and return a Blob (handles X-API-KEY auth).
   * Use this for download buttons — opens via URL.createObjectURL.
   */
  async exportPostPdf(postId: number, includeMedia: boolean = true): Promise<Blob> {
    const url = `${this.baseUrl}/posts/${postId}/export.pdf?include_media=${includeMedia}`;
    const headers: Record<string, string> = {};
    const token = getAccessToken();
    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    }
    if (API_KEY) {
      headers['X-API-KEY'] = API_KEY;
    }
    const response = await fetch(url, { headers });
    if (!response.ok) {
      const error = await response.text();
      throw new Error(`PDF export failed: ${response.status} - ${error}`);
    }
    return response.blob();
  }

  async exportPostThemedPdf(postId: number, opts: {
    theme?: string;
    logoUrl?: string;
    cover?: boolean | null;
    coverImageUrl?: string;
    starburstTheme?: string;
    designId?: string;
    coverLabel?: string;
    coverBullets?: string[];
    smartBreaks?: boolean;
  } = {}): Promise<Blob> {
    const params = new URLSearchParams({ theme: opts.theme || 'default' });
    if (opts.logoUrl) params.set('logo_url', opts.logoUrl);
    if (opts.cover === false) params.set('cover', 'false');
    if (opts.cover === true) params.set('cover', 'true');
    if (opts.coverImageUrl) params.set('cover_image_url', opts.coverImageUrl);
    if (opts.starburstTheme) params.set('starburst_theme', opts.starburstTheme);
    if (opts.designId) params.set('design_id', opts.designId);
    if (opts.coverLabel) params.set('cover_label', opts.coverLabel);
    if (opts.coverBullets?.length) params.set('cover_bullets', opts.coverBullets.join(','));
    if (opts.smartBreaks === false) params.set('smart_breaks', 'false');
    const url = `${this.baseUrl}/posts/${postId}/export-themed.pdf?${params}`;
    const headers: Record<string, string> = {};
    const token = getAccessToken();
    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    }
    if (API_KEY) {
      headers['X-API-KEY'] = API_KEY;
    }
    const response = await fetch(url, { headers });
    if (!response.ok) {
      const error = await response.text();
      throw new Error(`Themed PDF export failed: ${response.status} - ${error}`);
    }
    return response.blob();
  }

  /** Themes, mit denen der Beitrag exportiert werden kann (aus markdown-api). */
  async listExportThemes(postId: number): Promise<Array<{ id: string; description?: string; cover?: boolean }>> {
    const data = await this.request<{ themes: Array<{ id: string; description?: string; cover?: boolean }> }>(
      `/posts/${postId}/export-themes`,
    );
    return data.themes || [];
  }

  /**
   * PDF-Vorschau für den Text, der gerade im Editor steht (#2246). Der Server
   * baut dieselbe Anfrage wie beim Export, nur mit diesem Text.
   */
  async previewThemedPdf(
    postId: number,
    body: { markdown: string; theme: string },
    signal?: AbortSignal,
  ): Promise<ArrayBuffer> {
    const headers: Record<string, string> = { 'Content-Type': 'application/json' };
    const token = getAccessToken();
    if (token) headers['Authorization'] = `Bearer ${token}`;
    if (API_KEY) headers['X-API-KEY'] = API_KEY;
    const response = await fetch(`${this.baseUrl}/posts/${postId}/preview-themed.pdf`, {
      method: 'POST',
      headers,
      body: JSON.stringify(body),
      signal,
    });
    if (!response.ok) {
      let message = `${response.status}`;
      try {
        const data = await response.json();
        const detail = data?.detail;
        message = typeof detail === 'string' ? detail : detail?.message || detail?.hint || detail?.error || message;
      } catch { /* kein JSON */ }
      throw new Error(message);
    }
    return response.arrayBuffer();
  }

  async uploadToStorage(file: File, context?: string): Promise<{ id: number; url: string }> {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('filename', file.name);
    formData.append('context', context || 'content-frontend upload');
    formData.append('is_public', 'true');
    formData.append('skip_ai_safety', 'true');
    if (!STORAGE_API_KEY) {
      // #53: ohne Schluessel im Bundle ueber die cloud-api (Nutzer-Token).
      const { uploadViaCloudApi } = await import('./storage');
      const r = await uploadViaCloudApi(file);
      return { id: Number(r.id), url: r.url };
    }
    if (!STORAGE_API) {
      throw new Error('Storage ist auf dieser Instanz nicht konfiguriert (VITE_STORAGE_API)');
    }
    const storageHeaders: Record<string, string> = {};
    if (STORAGE_API_KEY) storageHeaders['X-API-KEY'] = STORAGE_API_KEY;
    const response = await fetch(`${STORAGE_API}/storage/upload`, {
      method: 'POST',
      headers: storageHeaders,
      body: formData,
    });
    if (!response.ok) {
      throw new Error(`Upload failed: ${response.status}`);
    }
    const data = await response.json();
    return {
      id: data.id,
      url: `${STORAGE_API}/storage/media/${data.id}`,
    };
  }

  // ── Doc Types ─────────────────────────────────────────────────────

  async getDocTypes(): Promise<string[]> {
    return this.request<string[]>('/doc-types');
  }

  async getTags(): Promise<string[]> {
    return this.request<string[]>('/tags');
  }

  async getLogos(): Promise<{ url: string; label: string }[]> {
    return this.request<{ url: string; label: string }[]>('/logos');
  }
}

// Singleton instance
export const apiClient = new ContentApiClient();

// Export for custom configurations
export { ContentApiClient };
